import {
  Typography,
  Container,
  Box,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Tab,
  Tabs,
  Skeleton,
} from '@mui/material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import axios, { pluckData } from 'src/utilities/http-client'
import { useEffect, useState } from 'react'
import { LineChart, PieChart } from '@mui/x-charts'

type GeneralStats = {
  logins: number
  uniqueLogins: number
  payingUsers: number
  payingUsersNotLinkedService: number
  loginToPayingPercentage: string
  loginToPayingRatio: string
  signupConversionRateAsPercentage: string
  churnRate: string
  linkedServiceUsers: number
  inactiveLinkedServiceUsers: number
  boundTreeUsers: number
  boundTreeFreeUsers: number
  boundTreeIndividualSubscribers: number
  boundTreeLinkedServiceUsers: number
  unsubscribedUsers: number
}

type UniqueLoginsOverTime = {
  dailyLabels: string[]
  dailyCounts: number[]
  weeklyLabels: string[]
  weeklyCounts: number[]
  monthlyLabels: string[]
  monthlyCounts: number[]
}

type SubscriptionGroupTrends = {
  labels: string[]
  unsubscribed: number[]
  paidIndividual: number[]
  paidBoundTree: number[]
  freeBoundTree: number[]
  boundTreeLinkedSvc: number[]
  linkedService: number[]
}

export default () => {
  let [dateLowerFilter, setDateLowerFilter] = useState<any>(
    dayjs().subtract(1, 'month').format(),
  )
  let [dateUpperFilter, setDateUpperFilter] = useState<any>(dayjs())
  let [generalStats, setGeneralStats] = useState<GeneralStats>()
  let [uniqueLoginsOverTime, setUniqueLoginsOverTime] =
    useState<UniqueLoginsOverTime>()
  let [subscriptionGroupTrends, setSubscriptionGroupTrends] =
    useState<SubscriptionGroupTrends>()
  let [chartTab, setChartTab] = useState('dailyTrend')
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [loading, setLoading] = useState(true)

  let getGeneralStats = () =>
    axios
      .get<GeneralStats>(
        `/api/scribe/auth-login-report/general-stats/${dayjs(dateLowerFilter).format('MM-DD-YYYY')}/${dayjs(dateUpperFilter).format('MM-DD-YYYY')}`,
      )
      .then(pluckData)
      .then((res) => {
        setGeneralStats(res)
      })
      .catch((err) =>
        setErrorMessage(
          `General stats for CAPCE failed to load: ${err.response?.data || err}`,
        ),
      )

  let getUniqueLoginsOverTime = () =>
    axios
      .get<UniqueLoginsOverTime>(
        `/api/scribe/auth-login-report/unique-logins-over-time/${dayjs(dateLowerFilter).format('MM-DD-YYYY')}/${dayjs(dateUpperFilter).format('MM-DD-YYYY')}`,
      )
      .then(pluckData)
      .then((res) => {
        setUniqueLoginsOverTime(res)
      })
      .catch((err) =>
        setErrorMessage(
          `Unique logins over time chart data failed to load: ${err.response?.data || err}`,
        ),
      )

  let getSubscriptionGroupTrends = () =>
    axios
      .get<SubscriptionGroupTrends>(
        `/api/scribe/auth-login-report/subscription-group-trends/${dayjs(dateLowerFilter).format('MM-DD-YYYY')}/${dayjs(dateUpperFilter).format('MM-DD-YYYY')}`,
      )
      .then(pluckData)
      .then((res) => {
        setSubscriptionGroupTrends(res)
      })
      .catch((err) =>
        setErrorMessage(
          `Subscription group trends data failed to load: ${err.response?.data || err}`,
        ),
      )

  useEffect(() => {
    if (dateLowerFilter && dateUpperFilter) {
      setLoading(true)
      Promise.all([
        getGeneralStats(),
        getUniqueLoginsOverTime(),
        getSubscriptionGroupTrends(),
      ])
        .then(() => setLoading(false))
        .catch((err) => {
          setErrorMessage(
            `CAPCE submission report failed to load: ${err.response?.data || err}`,
          )
        })
    }
  }, [dateLowerFilter, dateUpperFilter])

  return (
    <>
      <Container maxWidth="xl">
        {!loading ? (
          <>
            <Box className="grid grid-cols-6">
              <Box className="col-span-6 2xl:col-span-3">
                <Box>
                  <Typography
                    variant="h1"
                    sx={{ mb: 0, lineHeight: 'inherit' }}
                  >
                    Auth Login Report
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    *No data available before 5/2/2025
                  </Typography>
                </Box>
              </Box>
              <Box
                className="col-span-6 2xl:col-span-3"
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  mb: 2,
                  mt: 3,
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    sx={{ width: 160 }}
                    label="From"
                    value={dayjs(dateLowerFilter)}
                    slotProps={{ textField: { size: 'small' } }}
                    onChange={(newValue) => setDateLowerFilter(newValue)}
                  />
                  <DatePicker
                    sx={{ width: 160, ml: 2 }}
                    label="To"
                    value={dayjs(dateUpperFilter)}
                    slotProps={{ textField: { size: 'small' } }}
                    onChange={(newValue) => setDateUpperFilter(newValue)}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
            <Box className="grid grid-cols-12 gap-4 mt-4">
              <Box className="col-span-12 lg:col-span-3 md:col-span-6">
                <Card className="h-full">
                  <CardContent>
                    <Typography variant="h5">Unique logins</Typography>
                    <Typography variant="h2">
                      {generalStats?.uniqueLogins}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box className="col-span-12 lg:col-span-3 md:col-span-6">
                <Card className="h-full">
                  <CardContent>
                    <Typography variant="h5">Paying User Logins</Typography>
                    <Typography variant="h2">
                      {generalStats?.payingUsers}
                    </Typography>
                    <Typography variant="h6">
                      {generalStats?.loginToPayingPercentage} of Unique Logins
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box className="col-span-12 lg:col-span-3 md:col-span-6">
                <Card className="h-full">
                  <CardContent>
                    <Typography variant="h5">
                      Unique Login to Paying User Ratio
                    </Typography>
                    <Typography variant="h2">
                      {generalStats?.loginToPayingRatio}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box className="col-span-12 lg:col-span-3 md:col-span-6">
                <Card className="h-full">
                  <CardContent>
                    <Typography variant="h5">Signup Conversion Rate</Typography>
                    <Typography variant="h2">
                      {generalStats?.signupConversionRateAsPercentage}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box className="col-span-12 lg:col-span-3 md:col-span-6">
                <Card className="h-full">
                  <CardContent>
                    <Typography variant="h5">Churn Rate</Typography>
                    <Typography variant="h2">
                      {generalStats?.churnRate}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box className="col-span-12">
                <Tabs
                  variant="scrollable"
                  value={chartTab}
                  onChange={(e, val) => setChartTab(val)}
                >
                  <Tab value="dailyTrend" label="Daily Logins" />
                  <Tab value="weeklyTrend" label="Weekly Logins" />
                  <Tab value="monthlyTrend" label="Monthly Logins" />
                  <Tab
                    value="subscriptionGroup"
                    label="Logins By Subscription Type"
                  />
                </Tabs>
                {chartTab === 'dailyTrend' && (
                  <LineChart
                    series={[
                      {
                        data: uniqueLoginsOverTime?.dailyCounts ?? [],
                        label: 'Unique Logins per Day',
                      },
                    ]}
                    xAxis={[
                      {
                        scaleType: 'point',
                        data: uniqueLoginsOverTime?.dailyLabels ?? [],
                      },
                    ]}
                    yAxis={[{ min: 0 }]}
                    height={400}
                  />
                )}
                {chartTab === 'weeklyTrend' && (
                  <LineChart
                    series={[
                      {
                        data: uniqueLoginsOverTime?.weeklyCounts ?? [],
                        label: 'Unique Logins per Week',
                      },
                    ]}
                    xAxis={[
                      {
                        scaleType: 'point',
                        data: uniqueLoginsOverTime?.weeklyCounts ?? [],
                      },
                    ]}
                    yAxis={[{ min: 0 }]}
                    height={400}
                  />
                )}
                {chartTab === 'monthlyTrend' && (
                  <LineChart
                    series={[
                      {
                        data: uniqueLoginsOverTime?.monthlyCounts ?? [],
                        label: 'Unique Logins per Month',
                      },
                    ]}
                    xAxis={[
                      {
                        scaleType: 'point',
                        data: uniqueLoginsOverTime?.monthlyCounts ?? [],
                      },
                    ]}
                    yAxis={[{ min: 0 }]}
                    height={400}
                  />
                )}
                {chartTab === 'subscriptionGroup' && (
                  <LineChart
                    height={400}
                    xAxis={[
                      {
                        scaleType: 'point',
                        data: subscriptionGroupTrends?.labels,
                      },
                    ]}
                    series={[
                      {
                        data: subscriptionGroupTrends?.unsubscribed ?? [],
                        label: 'Unsubscribed',
                        stack: 'total',
                        showMark: false,
                        area: true,
                      },
                      {
                        data: subscriptionGroupTrends?.paidIndividual ?? [],
                        label: 'Paid Individual',
                        stack: 'total',
                        showMark: false,
                        area: true,
                      },
                      {
                        data: subscriptionGroupTrends?.paidBoundTree ?? [],
                        label: 'Paid Bound Tree',
                        stack: 'total',
                        showMark: false,
                        area: true,
                      },
                      {
                        data: subscriptionGroupTrends?.freeBoundTree ?? [],
                        label: 'Free Bound Tree',
                        stack: 'total',
                        showMark: false,
                        area: true,
                      },
                      {
                        data: subscriptionGroupTrends?.boundTreeLinkedSvc ?? [],
                        label: 'BoundTree LinkedSvc',
                        stack: 'total',
                        showMark: false,
                        area: true,
                      },
                      {
                        data: subscriptionGroupTrends?.linkedService ?? [],
                        label: 'Linked Service',
                        stack: 'total',
                        showMark: false,
                        area: true,
                      },
                    ]}
                    yAxis={[{ min: 0 }]}
                  />
                )}
              </Box>
              <Box className="col-span-12">
                <Card>
                  <CardContent>
                    <Typography variant="h6">
                      Composition of Unique Logins
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                      }}
                    >
                      <PieChart
                        series={[
                          {
                            data: [
                              {
                                id: 'paidIndividual',
                                value:
                                  generalStats?.payingUsersNotLinkedService ??
                                  0,
                                label: 'Payed Subscriber',
                              },
                              {
                                id: 'unsubscribed',
                                value: generalStats?.unsubscribedUsers ?? 0,
                                label: 'Unsubscribed',
                              },
                              {
                                id: 'linkedServicePaid',
                                value: generalStats?.linkedServiceUsers ?? 0,
                                label: 'Linked Service Sponsored',
                              },
                              {
                                id: 'boundTreeFree',
                                value: generalStats?.boundTreeFreeUsers ?? 0,
                                label: 'Bound Tree Free',
                              },
                              {
                                id: 'boundTreePaid',
                                value:
                                  generalStats?.boundTreeIndividualSubscribers ??
                                  0,
                                label: 'Bound Tree Subscribers',
                              },
                              {
                                id: 'boundTreeLinkedService',
                                value:
                                  generalStats?.boundTreeLinkedServiceUsers ??
                                  0,
                                label: 'Bound Tree Linked Service Sponsored',
                              },
                            ],
                          },
                        ]}
                        height={500}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </>
        ) : (
          // make Skeletons for the whole layout
          <Box className="grid grid-cols-12 gap-4 mt-4">
            <Box className="col-span-6 2xl:col-span-3">
              <Box>
                <Typography variant="h1" sx={{ mb: 0, lineHeight: 'inherit' }}>
                  <Skeleton />
                </Typography>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  <Skeleton />
                </Typography>
              </Box>
            </Box>
            <Box
              className="col-span-6 2xl:col-span-3"
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mb: 2,
                mt: 3,
              }}
            >
              <Skeleton width={160} height={40} />
              <Skeleton width={160} height={40} />
            </Box>
            <Box className="col-span-12 lg:col-span-3 md:col-span-6">
              <Skeleton height={136} />
            </Box>
            <Box className="col-span-12 lg:col-span-3 md:col-span-6">
              <Skeleton height={136} />
            </Box>
            <Box className="col-span-12 lg:col-span-3 md:col-span-6">
              <Skeleton height={136} />
            </Box>
            <Box className="col-span-12 lg:col-span-3 md:col-span-6">
              <Skeleton height={136} />
            </Box>
            <Box className="col-span-12">
              <Skeleton height={548} />
            </Box>
            <Box className="col-span-12">
              <Skeleton height={548} />
            </Box>
          </Box>
        )}
      </Container>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
